import React from 'react';

/**
 * Utility functions for determining content orientation
 */

export type ContentOrientation = 'portrait' | 'landscape' | 'square' | 'unknown';
export type ContentType = 'video' | 'image';

/**
 * Checks if content (video/image) is portrait or landscape
 * @param url - URL of the video or image
 * @param type - Type of content: 'video' or 'image'
 * @returns Promise that resolves to orientation
 */
export const getContentOrientation = (
  url: string,
  type: ContentType = 'video'
): Promise<ContentOrientation> => {
  return new Promise((resolve) => {
    if (!url) {
      resolve('unknown');
      return;
    }

    if (type === 'image') {
      const img = new Image();
      img.onload = () => {
        const aspectRatio = img.width / img.height;
        if (aspectRatio > 1.1) {
          resolve('landscape');
        } else if (aspectRatio < 0.9) {
          resolve('portrait');
        } else {
          resolve('square');
        }
      };
      img.onerror = () => resolve('unknown');
      img.src = url;
    } else if (type === 'video') {
      const video = document.createElement('video');
      video.onloadedmetadata = () => {
        const aspectRatio = video.videoWidth / video.videoHeight;
        if (aspectRatio > 1.1) {
          resolve('landscape');
        } else if (aspectRatio < 0.9) {
          resolve('portrait');
        } else {
          resolve('square');
        }
      };
      video.onerror = () => resolve('unknown');
      video.src = url;
    } else {
      resolve('unknown');
    }
  });
};

/**
 * Synchronous version that works with known dimensions
 * @param width - Width of the content
 * @param height - Height of the content
 * @returns Content orientation
 */
export const getOrientationFromDimensions = (
  width: number,
  height: number
): ContentOrientation => {
  if (!width || !height) return 'unknown';

  const aspectRatio = width / height;
  if (aspectRatio > 1.1) {
    return 'landscape';
  } else if (aspectRatio < 0.9) {
    return 'portrait';
  } else {
    return 'square';
  }
};

/**
 * Determines orientation from aspect ratio
 * @param aspectRatio - Width/Height ratio
 * @returns Content orientation
 */
export const getOrientationFromAspectRatio = (
  aspectRatio: number
): ContentOrientation => {
  if (aspectRatio > 1.1) {
    return 'landscape';
  } else if (aspectRatio < 0.9) {
    return 'portrait';
  } else {
    return 'square';
  }
};

/**
 * Hook for React components to get content orientation
 * @param url - URL of the content
 * @param type - Type of content
 * @returns Object with orientation and loading state
 */
export const useContentOrientation = (
  url: string,
  type: ContentType = 'video'
) => {
  const [orientation, setOrientation] = React.useState<ContentOrientation>('unknown');
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!url) {
      setOrientation('unknown');
      return;
    }

    setLoading(true);
    getContentOrientation(url, type)
      .then((result) => {
        setOrientation(result);
        setLoading(false);
      })
      .catch(() => {
        setOrientation('unknown');
        setLoading(false);
      });
  }, [url, type]);

  return { orientation, loading };
};
/**
 * Maps database orientation strings to internal ContentOrientation type
 * @param orientation - Orientation string from database
 * @returns ContentOrientation
 */
export function mapDatabaseOrientation(orientation?: string): ContentOrientation {
  if (!orientation) return 'unknown';
  const lower = orientation.toLowerCase();
  if (lower.includes('landscape') || lower.includes('horizontal')) return 'landscape';
  if (lower.includes('portrait') || lower.includes('vertical')) return 'portrait';
  if (lower.includes('square')) return 'square';
  return 'unknown';
}
